import { format } from 'date-fns'

/**
 * How far ahead a seasonal assignment may reach, in days from today.
 *
 * Mirrors `AVAILABLE_TO_HORIZON_DAYS` in property-service (activities.service)
 * and the horizon the PUT endpoint validates against: a range past it is
 * rejected with 400, so the admin blocks it before sending.
 */
export const SEASONAL_HORIZON_DAYS = 367

/**
 * Entity that owns the per-day assignments. Packages are `Rates` rows on the
 * backend, so they come back (and are deleted) as `rate`.
 */
export type SeasonalEntityKind = 'rate' | 'activity'

/** What the form targets: rates (rooms and packages) or activities. */
export type SeasonalTargetTab = 'rates' | 'activities'

/**
 * One row of the property-wide GET: consecutive days of one entity with the
 * same policy, already collapsed into a range by the server.
 */
export type SeasonalAssignmentRow = {
  kind: SeasonalEntityKind
  entityId: string
  entityName: string
  cancellationPolicyId: string
  cancellationPolicyName: string
  startDate: string
  endDate: string
}

/** Inclusive `YYYY-MM-DD` range, the unit the PUT endpoint accepts. */
export type DateRange = {
  startDate: string
  endDate: string
}

export type SeasonalRangeError =
  | 'required'
  | 'startAfterEnd'
  | 'past'
  | 'beyondHorizon'
  | 'overlap'

export type SeasonalFormValues = {
  rateIds: string[]
  activityIds: string[]
  ranges: DateRange[]
  cancellationPolicyId: string
}

export type SeasonalFormErrors = {
  targets: 'required' | null
  policy: 'required' | null
  ranges: (SeasonalRangeError | null)[]
}

export type BulkAssignmentPayload = {
  rateIds: string[]
  activityIds: string[]
  assignments: (DateRange & { cancellationPolicyId: string })[]
}

/**
 * Local calendar date as `YYYY-MM-DD`.
 *
 * "Today" is the hotelier's calendar day, so it is read in local time. Every
 * later step works on the string with UTC arithmetic, which never shifts a
 * day regardless of the browser's offset or DST.
 */
export function toISODate(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

// "Today" for a property is its own calendar day, not the admin's: an admin in
// another timezone would otherwise allow or block the wrong day. Without a
// timezone (still loading) or with one Intl rejects, it falls back to the
// browser's local day.
export function todayISOInTimeZone(timeZone?: string, now = new Date()): string {
  if (!timeZone) return toISODate(now)
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now)
    const { year, month, day } = Object.fromEntries(
      parts.map((p) => [p.type, p.value])
    )
    return `${year}-${month}-${day}`
  } catch {
    return toISODate(now)
  }
}

export function addDaysISO(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  const utc = new Date(Date.UTC(year, month - 1, day + days))
  return utc.toISOString().slice(0, 10)
}

/**
 * Local-midnight Date for a `YYYY-MM-DD` string. The date picker works with
 * local dates; `new Date('YYYY-MM-DD')` would parse as UTC and show the
 * previous day west of Greenwich.
 */
export function fromISODate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** GET window and edit bounds: today .. today + horizon, both inclusive. */
export function seasonalWindow(now: Date = new Date()): {
  from: string
  to: string
} {
  const from = toISODate(now)
  return { from, to: addDaysISO(from, SEASONAL_HORIZON_DAYS) }
}

/**
 * First pair of ranges sharing at least one day, as indexes into `ranges`, or
 * null. Ranges are inclusive, so touching ends (end = next start) overlap.
 */
export function rangesOverlap(ranges: DateRange[]): [number, number] | null {
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      if (overlaps(ranges[i], ranges[j])) return [i, j]
    }
  }
  return null
}

function overlaps(a: DateRange, b: DateRange): boolean {
  return a.startDate <= b.endDate && b.startDate <= a.endDate
}

/**
 * First validation error per range (index-aligned), or null when valid.
 * Mirrors the server validations so Save can be disabled instead of
 * round-tripping a 400.
 */
export function validateRanges(
  ranges: DateRange[],
  today: string,
  horizon: string
): (SeasonalRangeError | null)[] {
  const errors: (SeasonalRangeError | null)[] = ranges.map((range) => {
    if (!range.startDate || !range.endDate) return 'required'
    if (range.startDate > range.endDate) return 'startAfterEnd'
    // The server tolerates a few past days, but the list window starts today,
    // so a past range could never be seen or removed from this screen.
    if (range.startDate < today) return 'past'
    if (range.endDate > horizon) return 'beyondHorizon'
    return null
  })

  // Overlap is only checked between ranges that are otherwise valid, and every
  // range involved is flagged (not just the first pair) so the user sees all
  // conflicts at once. The server rejects overlapping ranges in one payload.
  const valid = errors.map((error) => error === null)
  ranges.forEach((range, i) => {
    if (!valid[i]) return
    if (
      ranges.some((other, j) => j !== i && valid[j] && overlaps(range, other))
    ) {
      errors[i] = 'overlap'
    }
  })

  return errors
}

/** Whole-form validation. The form needs no range-level policy: one policy
 * applies to every target and range of a single save. */
export function validateSeasonalForm(
  values: SeasonalFormValues,
  today: string,
  horizon: string
): SeasonalFormErrors {
  return {
    targets:
      values.rateIds.length + values.activityIds.length === 0
        ? 'required'
        : null,
    policy: values.cancellationPolicyId ? null : 'required',
    ranges: validateRanges(values.ranges, today, horizon),
  }
}

export function isSeasonalFormValid(errors: SeasonalFormErrors): boolean {
  return (
    errors.targets === null &&
    errors.policy === null &&
    errors.ranges.length > 0 &&
    errors.ranges.every((error) => error === null)
  )
}

/** Body of the property-level PUT: every target x every range, one policy. */
export function toBulkPayload(values: SeasonalFormValues): BulkAssignmentPayload {
  return {
    rateIds: values.rateIds,
    activityIds: values.activityIds,
    assignments: [...values.ranges]
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .map(({ startDate, endDate }) => ({
        startDate,
        endDate,
        cancellationPolicyId: values.cancellationPolicyId,
      })),
  }
}

/** Case-insensitive match on the target name or the policy name. */
export function filterAssignments(
  rows: SeasonalAssignmentRow[],
  search: string
): SeasonalAssignmentRow[] {
  const needle = search.trim().toLowerCase()
  if (!needle) return rows
  return rows.filter(
    (row) =>
      row.entityName.toLowerCase().includes(needle) ||
      row.cancellationPolicyName.toLowerCase().includes(needle)
  )
}

/**
 * Keeps only Active policies. The property list endpoint already filters
 * Active server-side and does not return `status`, so a policy without the
 * field is treated as Active; an explicit non-Active status is dropped in case
 * the endpoint starts returning archived ones.
 */
export function activePolicies<T extends object>(policies: T[]): T[] {
  return policies.filter((policy) => {
    const status = (policy as { status?: unknown }).status
    return status === undefined || status === null || status === 'Active'
  })
}
